/**
 * Language utility functions
 * Provides helpers for working with i18n language codes
 */

/**
 * Extracts the base language code from a full locale string
 * @param {string} languageCode - Full language code (e.g., 'bg-BG', 'en-US')
 * @returns {string} Base language code (e.g., 'bg', 'en')
 *
 * @example
 * getBaseLanguage('bg-BG') // returns 'bg'
 * getBaseLanguage('en-US') // returns 'en'
 * getBaseLanguage('bg') // returns 'bg'
 */
export const getBaseLanguage = (languageCode) => {
  if (!languageCode) return 'bg'; // Default fallback
  return languageCode.split('-')[0];
};

/**
 * Gets the current base language from i18n instance
 * @param {Object} i18n - i18next instance
 * @returns {string} Base language code
 *
 * @example
 * const currentLang = getCurrentLanguage(i18n); // returns 'bg' or 'en'
 */
export const getCurrentLanguage = (i18n) => {
  return getBaseLanguage(i18n.language);
};

/**
 * Common language codes used in the application
 */
export const LANGUAGES = {
  BG: 'bg',
  EN: 'en',
};

/**
 * Language display names
 */
export const LANGUAGE_NAMES = {
  bg: 'Български',
  en: 'English',
};

/**
 * Default language, served from the site root without a path prefix
 */
export const DEFAULT_LANGUAGE = LANGUAGES.BG;

/**
 * Canonical origin of the production site (no trailing slash)
 */
export const SITE_URL = 'https://interior-sg.com';

/**
 * Reads the active language from a URL path.
 * The path is the single source of truth for the language, so that every page
 * has exactly one crawlable URL per language.
 *
 * @param {string} pathname - Browser path (e.g. '/en/about')
 * @returns {string} 'en' for /en-prefixed paths, otherwise 'bg'
 *
 * @example
 * getLangFromPath('/en/about') // 'en'
 * getLangFromPath('/en')       // 'en'
 * getLangFromPath('/about')    // 'bg'
 */
export const getLangFromPath = (pathname = '/') => {
  return /^\/en(\/|$)/.test(pathname) ? LANGUAGES.EN : DEFAULT_LANGUAGE;
};

/**
 * React Router basename for a language. With this set, every `<Link to="/about">`
 * in the app automatically points at the correct localized URL.
 *
 * @param {string} lang - Base language code
 * @returns {string} '/en' for English, '/' for the default language
 */
export const getRouterBasename = (lang) => (lang === LANGUAGES.EN ? '/en' : '/');

/**
 * Builds the absolute, canonical URL of a route in a given language.
 * Used for rel=canonical, hreflang and the sitemap, so all three stay in sync.
 *
 * @param {string} lang - Base language code
 * @param {string} routePath - Router path without language prefix (e.g. '/about')
 * @returns {string} Absolute URL without a trailing slash (except the BG root)
 *
 * @example
 * buildLocalizedUrl('bg', '/about') // 'https://interior-sg.com/about'
 * buildLocalizedUrl('en', '/about') // 'https://interior-sg.com/en/about'
 * buildLocalizedUrl('en', '/')      // 'https://interior-sg.com/en'
 */
export const buildLocalizedUrl = (lang, routePath = '/') => {
  const prefix = lang === LANGUAGES.EN ? '/en' : '';
  const path = routePath === '/' ? '' : routePath.replace(/\/+$/, '');
  const url = `${SITE_URL}${prefix}${path}`;

  // The BG homepage collapses to the bare origin — keep its trailing slash
  return url === SITE_URL ? `${SITE_URL}/` : url;
};
